import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { today as todayApi } from '@/utils/api';
import { Sun, AlertTriangle, Clock, DollarSign, UserPlus, GitBranch, Sparkles, RefreshCw } from 'lucide-react';
import SetupChecklist from '@/components/today/SetupChecklist';
import FollowupDrafter from '@/components/today/FollowupDrafter';

export default function Today() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [drafter, setDrafter] = useState(null); // { kind, id, label } | null
  const navigate = useNavigate();

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await todayApi.get();
      setData(r.data);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="p-6">
      <div className="h-10 w-40 rounded-btn animate-pulse mb-4" style={{ background: 'var(--border)' }} />
      <div className="grid md:grid-cols-2 gap-3">
        {[1,2,3,4].map(i => <div key={i} className="h-28 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />)}
      </div>
    </div>
  );
  if (!data) return null;

  // Build a flat, prioritised list: bugs (P0) → invoices (P1) → leads (P1) → changes (P2)
  const items = [
    ...data.overdue_bugs.map(b => ({ tier: 'P0', kind: 'bug', title: b.title, meta: b.severity, onClick: () => navigate(`/admin/clients`) })),
    ...data.unpaid_invoices.map(i => ({ tier: 'P1', kind: 'invoice', title: i.invoice_number, meta: `$${(i.total||0).toFixed(2)}`, onClick: () => navigate(`/admin/clients`) })),
    ...(data.leads_pending ? [{ tier: 'P1', kind: 'leads', title: `${data.leads_pending} lead${data.leads_pending>1?'s':''} awaiting approval`, meta: '', onClick: () => navigate('/admin/leads') }] : []),
    ...(data.change_requests_pending ? [{ tier: 'P2', kind: 'change', title: `${data.change_requests_pending} change request${data.change_requests_pending>1?'s':''} pending`, meta: '', onClick: () => {} }] : []),
  ];
  const empty = items.length === 0;
  const tierColor = { P0: '#B34A3A', P1: '#D9A441', P2: '#5B2D8E' };
  const kindIcon = { bug: AlertTriangle, invoice: DollarSign, leads: UserPlus, change: GitBranch };

  return (
    <div className="p-6 animate-fade-in max-w-5xl" data-testid="today-page">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-btn flex items-center justify-center" style={{ background: '#D9A44120' }}>
            <Sun size={20} style={{ color: '#D9A441' }} />
          </div>
          <div>
            <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>Today</h1>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>Your AI chief-of-staff's priority list</p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-btn font-semibold"
          style={{ background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--border)' }}
          data-testid="today-refresh-btn"
        >
          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <SetupChecklist />

      {data.nudge && (
        <div className="card-base p-5 mb-5" style={{ background: 'linear-gradient(135deg, #5B2D8E 0%, #3D1B4E 100%)', color: 'white' }} data-testid="ai-nudge">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-btn flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.15)' }}>
              <Sparkles size={14} style={{ color: '#F5C651' }} />
            </div>
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-widest font-bold mb-1.5" style={{ color: '#F5C651' }}>AI priority brief</p>
              <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#FFFFFF' }}>{data.nudge}</p>
            </div>
          </div>
        </div>
      )}

      {/* Counts row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard icon={AlertTriangle} color="#B34A3A" label="Overdue bugs" value={data.overdue_bugs.length} testid="today-count-bugs" />
        <StatCard icon={DollarSign} color="#D9A441" label="Unpaid 14d+" value={data.unpaid_invoices.length} testid="today-count-invoices" />
        <StatCard icon={UserPlus} color="#006080" label="Leads pending" value={data.leads_pending} onClick={() => navigate('/admin/leads')} testid="today-count-leads" />
        <StatCard icon={GitBranch} color="#5B2D8E" label="Changes pending" value={data.change_requests_pending} testid="today-count-changes" />
      </div>

      {/* Prioritised list */}
      {empty ? (
        <div className="card-base p-10 text-center" data-testid="today-empty">
          <div className="text-4xl mb-3">🎉</div>
          <p className="text-lg font-bold mb-1" style={{ color: 'var(--ink)' }}>Inbox zero</p>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>No overdue items, no pending leads. Enjoy the day.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="today-items">
          <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--muted)' }}>Tackle in this order</p>
          {items.map((it, i) => {
            const Icon = kindIcon[it.kind] || Clock;
            return (
              <div
                key={i}
                className="card-base p-3 w-full flex items-center gap-3 text-left hover:shadow transition-shadow"
                data-testid={`today-item-${i}`}
              >
                <button onClick={it.onClick} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                  <div className="w-6 h-6 rounded-btn flex items-center justify-center flex-shrink-0" style={{ background: `${tierColor[it.tier]}20`, color: tierColor[it.tier] }}>
                    <Icon size={12} />
                  </div>
                  <span className="font-mono text-[10px] font-bold flex-shrink-0" style={{ color: tierColor[it.tier] }}>{it.tier}</span>
                  <span className="text-sm flex-1 truncate" style={{ color: 'var(--ink)' }}>{it.title}</span>
                  {it.meta && <span className="text-xs font-mono flex-shrink-0" style={{ color: 'var(--muted)' }}>{it.meta}</span>}
                </button>
                {it.draftKind && it.draftId && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setDrafter({ kind: it.draftKind, id: it.draftId, label: it.title }); }}
                    className="text-xs flex items-center gap-1 px-2 py-1 rounded-btn font-semibold flex-shrink-0"
                    style={{ background: '#5B2D8E15', color: '#5B2D8E' }}
                    title="Draft AI follow-up email"
                    data-testid={`today-draft-${it.draftKind}-${i}`}
                  >
                    <Sparkles size={11} /> Draft
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-1 text-xs mt-6" style={{ color: 'var(--muted)' }}>
        <Clock size={10} /> as of {new Date(data.as_of).toLocaleTimeString()}
      </div>

      {drafter && (
        <FollowupDrafter
          open={!!drafter}
          kind={drafter.kind}
          id={drafter.id}
          itemLabel={drafter.label}
          onClose={() => setDrafter(null)}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, onClick, testid }) {
  const Wrap = onClick ? 'button' : 'div';
  return (
    <Wrap
      onClick={onClick}
      className={`card-base p-3 flex items-center gap-2.5 ${onClick ? 'card-interactive cursor-pointer text-left w-full' : ''}`}
      data-testid={testid}
    >
      <div className="w-8 h-8 rounded-btn flex items-center justify-center flex-shrink-0" style={{ background: `${color}20` }}>
        <Icon size={14} style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-black leading-tight" style={{ color }}>{value}</p>
        <p className="text-[10px] uppercase tracking-wider font-semibold truncate" style={{ color: 'var(--muted)' }}>{label}</p>
      </div>
    </Wrap>
  );
}
